import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { catalogApi } from "../../api/axios";

/**
 * Get categories
 */
export const getAttributes = createAsyncThunk(
  "attribute/getAttributes",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/attributes?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get attributes key/ value
 */
export const getAttributesKeyValue = createAsyncThunk(
  "attribute/getAttributesKeyValue",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/attributes/key-value?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Add attribute
 */
export const addAttribute = createAsyncThunk(
  "attribute/addAttribute",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.post("/v1/products/attributes", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update attribute
 */
export const updateAttribute = createAsyncThunk(
  "attribute/updateAttribute",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.put("/v1/products/attributes", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete attribute
 */
export const attributeDelete = createAsyncThunk(
  "attribute/attributeDelete",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch(
        "/v1/products/attributes/actions",
        obj
      );
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return deleted payload to update state
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Perform attribute action
 */
export const attributeAction = createAsyncThunk(
  "attribute/attributeAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch(
        "/v1/products/attributes/actions",
        obj
      );
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated attribute action info
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Slice
const attributeSlice = createSlice({
  name: "attributes",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearAttributeState: (state) => {
      state.list = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET ATTRIBUTES
      .addCase(getAttributes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAttributes.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(getAttributes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ADD
      .addCase(addAttribute.pending, (state) => {
        state.loading = true;
      })
      .addCase(addAttribute.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = {};
      })
      .addCase(addAttribute.rejected, (state, action) => {
        state.loading = false;
      })
      // UPDATE
      .addCase(updateAttribute.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAttribute.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = {};
      })
      .addCase(updateAttribute.rejected, (state, action) => {
        state.loading = false;
      })

      // DELETE
      .addCase(attributeDelete.fulfilled, (state, action) => {
        const deleted = action.payload;
        state.list = state.list.filter((a) => a.id !== deleted.id);
      })

      // ACTION (e.g. activate/deactivate)
      .addCase(attributeAction.fulfilled, (state, action) => {
        const { uid, record_status } = action.payload;
        state.list = state.list.map((a) =>
          a.uid === uid ? { ...a, record_status } : a
        );
      });
  },
});

export const { clearAttributeState } = attributeSlice.actions;
export default attributeSlice.reducer;
